// app/javascript/pages/Admin/TermsVersions/New.tsx
import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import {
  ShieldAlert,
  ArrowLeft,
  Save,
  CheckCircle2,
  FileText,
  Eye,
  Edit3
} from 'lucide-react'

interface Props {
  default_version_number: string
  default_content: string
}

export default function TermsVersionsNew({ default_version_number, default_content }: Props) {
  const [versionNumber, setVersionNumber] = useState(default_version_number || '1.1')
  const [title, setTitle] = useState('Terms of Service and End-User License Agreement')
  const [summary, setSummary] = useState('')
  const [content, setContent] = useState(default_content || '')
  const [activateImmediately, setActivateImmediately] = useState(false)
  const [previewTab, setPreviewTab] = useState<'edit' | 'preview'>('edit')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!versionNumber.trim() || !title.trim() || !content.trim()) {
      alert('Please complete version number, title, and terms content.')
      return
    }

    setSubmitting(true)
    router.post(
      '/admin/terms_versions',
      {
        terms_version: {
          version_number: versionNumber.trim(),
          title: title.trim(),
          summary: summary.trim(),
          content: content.trim(),
        },
        activate_immediately: activateImmediately ? '1' : '0',
      },
      {
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="h-9 w-9 p-0">
              <Link href="/admin/terms_versions">
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Draft New Terms Version
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Create a revised or amended version of the legal terms and disclaimers.
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border">
              <CardTitle className="text-base font-bold text-foreground">
                Version Details & Metadata
              </CardTitle>
              <CardDescription className="text-xs text-muted-foreground">
                Configure semantic versioning and high-level summaries for users.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="version_number" className="text-xs font-semibold text-foreground">
                    Version Number *
                  </Label>
                  <Input
                    id="version_number"
                    value={versionNumber}
                    onChange={(e) => setVersionNumber(e.target.value)}
                    placeholder="e.g. 1.1"
                    required
                    className="font-mono text-sm"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Format like 1.1, 1.2, 2.0 (must be unique).
                  </p>
                </div>

                <div className="sm:col-span-2 space-y-2">
                  <Label htmlFor="title" className="text-xs font-semibold text-foreground">
                    Document Title *
                  </Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Terms of Service and End-User License Agreement"
                    required
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary" className="text-xs font-semibold text-foreground">
                  Summary / Change Highlights
                </Label>
                <Input
                  id="summary"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="e.g. Updated Section 4 to explicitly cover newly integrated LLM features and audit logging."
                  className="text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Brief description displayed to users in the acceptance modal and public page.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Content Editor Card */}
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-foreground">
                  Legal Agreement Content *
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Full legal text with disclaimers, waivers, and user obligations.
                </CardDescription>
              </div>

              {/* Edit / Preview Tabs */}
              <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => setPreviewTab('edit')}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                    previewTab === 'edit'
                      ? 'bg-card text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>Editor</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTab('preview')}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                    previewTab === 'preview'
                      ? 'bg-card text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Preview</span>
                </button>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              {previewTab === 'edit' ? (
                <div className="space-y-2">
                  <textarea
                    id="content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={22}
                    required
                    placeholder="Enter full legal text in markdown/text format..."
                    className="w-full font-mono text-xs sm:text-sm p-4 rounded-xl border border-border bg-background text-foreground leading-relaxed focus:outline-hidden focus:ring-1 focus:ring-primary resize-y"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Markdown headings (`## Section`), bullet lists, and paragraphs are supported.
                  </p>
                </div>
              ) : (
                <div className="p-6 rounded-xl border border-border bg-background min-h-[400px] max-h-[500px] overflow-y-auto text-foreground leading-relaxed">
                  <KatexRenderer
                    content={content || 'Nothing to preview.'}
                    format="markdown"
                    disableExamHelpers
                    className="markdown-content text-xs sm:text-sm leading-relaxed"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activation & Action Footer */}
          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={activateImmediately}
                  onChange={(e) => setActivateImmediately(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-ring focus:ring-offset-background cursor-pointer"
                />
                <div>
                  <span className="text-sm font-semibold text-foreground block">
                    Activate immediately upon saving
                  </span>
                  <span className="text-xs text-muted-foreground">
                    If checked, this version becomes effective now and all users will be prompted to re-accept on next login.
                  </span>
                </div>
              </label>

              <div className="flex items-center gap-3 self-end sm:self-auto">
                <Button asChild variant="outline">
                  <Link href="/admin/terms_versions">Cancel</Link>
                </Button>

                <Button type="submit" disabled={submitting} className="gap-2 font-semibold">
                  <Save className="w-4 h-4" />
                  <span>{submitting ? 'Saving...' : 'Save Terms Version'}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </AppShell>
  )
}
